import { ref, watch, inject } from 'vue';
import { apiCall } from '../../../api/services/apiCalls';
import { camelCase } from '../../../common/utils';
import { sendClickAnalytics } from '../../../utils';
import type { centerState } from '../models/types/ColumnsCenterState';

interface ArticleContent {
  bodyHtml: string;
  id: string;
  name: string;
}

const emptyArticle = (): ArticleContent => ({ bodyHtml: '', id: '', name: '' });

/**
* Encapsulates the fetch + analytics flow for a help-center article driven by
* `mainState.articleId`. Used by both the desktop column display and the
* mobile modal so the data layer stays consistent across viewports.
*/
export default function useArticleContent() {
  const articleContent = ref<ArticleContent>(emptyArticle());
  const isLoading = ref(false);
  const error = ref<unknown>(null);

  const { mainState } = inject<centerState>('centerState');

  const articleMetricHandler = () => {
    const { adobeName } = mainState.value;
    const articleTitle = camelCase(articleContent.value.name || '');
    sendClickAnalytics({
      pageName: '',
      linkName: `${adobeName}_${articleTitle}`,
    });
  };

  // Explicit cleanup for consumers that need to discard the current article
  // (e.g. the mobile modal when it closes). Not invoked automatically on an
  // empty articleId to preserve the desktop behavior of keeping the last
  // rendered article visible.
  const reset = () => {
    articleContent.value = emptyArticle();
    error.value = null;
    isLoading.value = false;
  };

  watch(
    () => mainState.value.articleId,
    async (newId) => {
      if (!newId) return;
      isLoading.value = true;
      error.value = null;
      try {
        const [response] = await apiCall(
          'default',
          'all-articles',
          newId,
          'articleIds',
        );
        if (response && response.constructor === Object) {
          articleContent.value = { ...articleContent.value, ...response };
          articleMetricHandler();
        }
      } catch (err) {
        error.value = err;
        // eslint-disable-next-line no-console
        console.error('API Call Failed:', err);
      } finally {
        isLoading.value = false;
      }
    },
  );

  return {
    articleContent,
    isLoading,
    error,
    reset,
  };
}
